import classNames from 'classnames';
import * as React from 'react';
import { usePlannerTranslations } from '../../Planner/Context/PlannerTranslationsContextProvider';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { Theme } from '~/Utils/Theme';
import styles from './SingleSubjectAction.module.css';
import type { SingleSubjectActionDisplayType } from './SingleSubjectActionDisplayType';

export interface SingleSubjectActionProps {
    isSelected: boolean;
    isDisabled: boolean;
    onClick: () => void;
    actionDisplayType?: SingleSubjectActionDisplayType;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const SingleSubjectAction: React.FC<SingleSubjectActionProps> = props => {
    const contextualTheme = useTheme();
    const { onClick, isSelected, isDisabled, actionDisplayType, theme = contextualTheme } = props;
    const { withRoundedCorners } = theme;

    const { actions } = usePlannerTranslations();
    const { removeSubject, addSubject } = actions;

    const displayAsIcon = actionDisplayType === 'icon';
    const buttonSize = displayAsIcon ? ButtonSize.XSmall : ButtonSize.Small;

    const label = isSelected ? removeSubject : addSubject;
    const iconName = isSelected ? 'close' : 'plus';
    const buttonType = isSelected ? ButtonType.Outline : ButtonType.Solid;
    const buttonTitle = displayAsIcon ? label : undefined;

    const renderInternalContent = () =>
        displayAsIcon ? (
            <>
                <span className="visuallyHidden">{label}</span>
                <SvgIcon className={styles.Icon} iconName={iconName} />
            </>
        ) : (
            <span>{label}</span>
        );

    return (
        <Button
            className={classNames(
                styles.SingleSubjectAction,
                isSelected && styles['Variant--selected'],
                displayAsIcon && styles['Variant--icon'],
                withRoundedCorners && styles['Variant--rounded']
            )}
            onClick={onClick}
            size={buttonSize}
            type={buttonType}
            tier={ButtonTier.Primary}
            title={buttonTitle}
            disabled={isDisabled}
        >
            {renderInternalContent()}
        </Button>
    );
};
